import { useState, useEffect, useCallback } from "react";
import { logger } from "@/utils/logger";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Volume2, VolumeX, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { recordGameWon } from "@/utils/playerProfile";

import { getVoiceLang } from "@/utils/voicePref";
const HARI = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu", "Minggu"];
const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

const speakText = (t) => {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(t);
    u.lang = getVoiceLang(); u.rate = 0.85; u.pitch = 1.0;
    window.speechSynthesis.speak(u);
  } catch (err) { logger.debug("speakText failed", err); }
};

const BelajarHariBulan = ({ onBackToMenu }) => {
  const { width: winW, height: winH } = useWindowSize();
  const [mode, setMode] = useState("hari"); // hari | bulan
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);
  const [target, setTarget] = useState(null);
  const [opts, setOpts] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [soundOn, setSoundOn] = useState(true);
  const [won, setWon] = useState(false);
  const totalRounds = 12;

  const newQuestion = useCallback(() => {
    const list = mode === "hari" ? HARI : BULAN;
    const correct = list[Math.floor(Math.random() * list.length)];
    const wrongs = [];
    while (wrongs.length < 3) {
      const w = list[Math.floor(Math.random() * list.length)];
      if (w !== correct && !wrongs.includes(w)) wrongs.push(w);
    }
    const all = [...wrongs, correct].sort(() => Math.random() - 0.5);
    setTarget(correct); setOpts(all); setFeedback(null);
    const idx = list.indexOf(correct) + 1;
    setTimeout(() => { if (soundOn) speakText(`Click ${mode} to ${idx}, which is ${correct}`); }, 300);
  }, [mode, soundOn]);

  useEffect(() => { setScore(0); setRound(0); setWon(false); newQuestion(); }, [mode, newQuestion]);

  const handlePick = (a) => {
    if (feedback) return;
    if (a === target) {
      setFeedback({ type: "ok", text: `Benar! ${a}!` });
      setScore(s => s + 1);
      if (soundOn) speakText(`Correct! ${a}!`);
    } else {
      setFeedback({ type: "wrong", text: `Itu ${a}` });
      if (soundOn) speakText(`The correct answer is ${target}`);
    }
    setTimeout(() => {
      if (round + 1 >= totalRounds) { setWon(true); recordGameWon("haribulan"); }
      else { setRound(r => r + 1); newQuestion(); }
    }, 1500);
  };

  if (won) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-4" data-testid="haribulan-win">
        <Confetti width={winW} height={winH} recycle={false} numberOfPieces={250} />
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="glass-card-strong rounded-2xl p-8 text-center max-w-md w-full">
          <span className="text-6xl block mb-3">📅</span>
          <h2 className="font-fredoka text-3xl text-gold font-bold mb-2">Selesai!</h2>
          <p className="font-fredoka text-gold-light/80 mb-4">Skor: {score}/{totalRounds}</p>
          <div className="flex flex-col gap-2">
            <Button onClick={() => { setScore(0); setRound(0); setWon(false); newQuestion(); }}
              className="w-full font-fredoka font-bold rounded-xl h-10 bg-gradient-to-r from-yellow-400 to-amber-500"><RotateCcw className="w-4 h-4 mr-2" />Main Lagi</Button>
            <Button onClick={() => setMode(mode === "hari" ? "bulan" : "hari")} variant="outline" className="w-full glass-card border-gold/20 text-gold font-fredoka rounded-xl">Ganti ke {mode === "hari" ? "Bulan" : "Hari"}</Button>
            <Button onClick={onBackToMenu} variant="outline" className="w-full glass-card border-gold/20 text-gold font-fredoka rounded-xl">Menu</Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center w-full max-w-4xl mx-auto px-3 pt-3" data-testid="haribulan-game">
      <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
        <span className="font-fredoka text-gold font-bold text-sm">📅 {round + 1}/{totalRounds} • Skor: {score}</span>
        <div className="flex gap-1.5">
          <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 w-7 p-0">{soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}</Button>
          <Button onClick={onBackToMenu} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Home className="w-3 h-3" /></Button>
        </div>
      </div>

      <div className="flex gap-2 mb-3 w-full">
        <Button data-testid="haribulan-mode-hari" onClick={() => setMode("hari")} className={`flex-1 font-fredoka font-bold rounded-xl h-10 ${mode === "hari" ? "bg-gold text-brown-dark" : "glass-card text-gold"}`}>📅 Hari</Button>
        <Button data-testid="haribulan-mode-bulan" onClick={() => setMode("bulan")} className={`flex-1 font-fredoka font-bold rounded-xl h-10 ${mode === "bulan" ? "bg-gold text-brown-dark" : "glass-card text-gold"}`}>🗓️ Bulan</Button>
      </div>

      <motion.div key={`${mode}-${round}`} initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="glass-card-strong rounded-2xl p-6 w-full text-center mb-3">
        <p className="font-fredoka text-gold-light/70 text-sm mb-1">{mode === "hari" ? "Hari" : "Bulan"} ke:</p>
        <p className="font-fredoka text-7xl text-gold font-black mb-1">{target ? (mode === "hari" ? HARI : BULAN).indexOf(target) + 1 : ""}</p>
      </motion.div>

      <AnimatePresence>
        {feedback && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ opacity: 0 }} className="mb-3" data-testid={`haribulan-feedback-${feedback.type}`}>
            <span className={`font-fredoka font-bold text-base px-4 py-2 rounded-full ${feedback.type === "ok" ? "bg-green-500 text-white" : "bg-red-400 text-white"}`}>
              {feedback.type === "ok" ? "✅" : "❌"} {feedback.text}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-2 gap-4 w-full">
        {opts.map((a, i) => (
          <motion.button key={`${a}-${i}`} data-testid={`haribulan-opt-${i}`}
            onClick={() => handlePick(a)} disabled={!!feedback}
            whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.95 }}
            className="ingredient-3d quiz-option-btn text-center text-slate-800 font-fredoka font-bold">
            {a}
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export default BelajarHariBulan;
